import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Card,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  TablePagination,
  Alert,
  Snackbar,
  Stack,
  Divider
} from '@mui/material';
import {
  List as ListIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import useSWR from 'swr';
import axios from 'axios';
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { apiHost } from '../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

// Create an Axios instance with the dynamic base URL and credentials
const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
});

// Fetcher function for useSWR
const fetcher = (url) => axiosInstance.get(url).then((res) => res.data);

export const CustomerTransaksi = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedTransaksi, setSelectedTransaksi] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Build query parameters
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('page', page + 1);
    params.append('limit', rowsPerPage);
    
    if (searchTerm) params.append('search', searchTerm);
    if (statusFilter) params.append('status', statusFilter);
    if (paymentFilter) params.append('payment', paymentFilter);
    if (dateFilter) params.append('date', dateFilter);
    
    return params.toString();
  };

  // Fetch data with SWR
  const { data, error, isLoading, mutate } = useSWR(
    `/transaksicustomer?${buildQueryParams()}`,
    fetcher,
    {
      refreshInterval: 30000, // Refresh every 30 seconds
      revalidateOnFocus: false,
    }
  );

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Handle status filter
  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  // Handle payment filter
  const handlePaymentFilter = (event) => {
    setPaymentFilter(event.target.value);
    setPage(0);
  };

  // Handle date filter
  const handleDateFilter = (event) => {
    setDateFilter(event.target.value);
    setPage(0);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPaymentFilter('');
    setDateFilter('');
    setPage(0);
  };

  // Open detail modal
  const handleOpenDetailModal = (transaksi) => {
    setSelectedTransaksi(transaksi);
    setOpenDetailModal(true);
  };

  // Close detail modal
  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedTransaksi(null);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'settlement':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get payment method color
  const getPaymentColor = (payment) => {
    switch (payment) {
      case 'cash':
        return 'primary';
      case 'qris':
        return 'secondary';
      case 'transfer':
        return 'info';
      default:
        return 'default';
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading data: {error.message}
      </Alert>
    );
  }

  const transaksiData = data?.data || [];
  const totalData = data?.totalData || 0;

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <ListIcon />
              <Typography variant="h6">Transaksi Customer</Typography>
            </Box>
          }
        //   action={
        //     <Button
        //       variant="contained"
        //       startIcon={<AddIcon />}
        //       onClick={() => {/* Handle add new transaction */}}
        //     >
        //       Tambah Transaksi
        //     </Button>
        //   }
        />

        {/* Filters */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Cari Customer/Order ID"
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  label="Status"
                >
                  <MenuItem value="">Semua</MenuItem>
                  <MenuItem value="settlement">Settlement</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Pembayaran</InputLabel>
                <Select
                  value={paymentFilter}
                  onChange={handlePaymentFilter}
                  label="Pembayaran"
                >
                  <MenuItem value="">Semua</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="qris">QRIS</MenuItem>
                  
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Tanggal"
                value={dateFilter}
                onChange={handleDateFilter}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                fullWidth
              >
                Bersihkan Filter
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Uang Customer</TableCell>
                <TableCell>Tanggal</TableCell>
                <TableCell>Pembayaran</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transaksiData.map((transaksi) => (
                <TableRow key={transaksi.uuid}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {transaksi.order_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {transaksi.namaCustomer}
                      </Typography>
                      {transaksi.Customer?.email && (
                        <Typography variant="caption" color="text.secondary">
                          {transaksi.Customer.email}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(transaksi.totaljual)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(transaksi.uangCustomer || 'Qris')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {formatDate(transaksi.tanggal)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaksi.pembayaran.toUpperCase()}
                      size="small"
                      color={getPaymentColor(transaksi.pembayaran)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaksi.status_pembayaran.toUpperCase()}
                      size="small"
                      color={getStatusColor(transaksi.status_pembayaran)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDetailModal(transaksi)}
                      >
                        <ViewIcon />
                      </IconButton>
                   
                     
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalData}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Baris per halaman:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} dari ${count !== -1 ? count : `lebih dari ${to}`}`
          }
        />
      </Card>

      {/* Detail Modal */}
      <Dialog
        open={openDetailModal}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Detail Transaksi</Typography>
            <IconButton onClick={handleCloseDetailModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTransaksi && (
            <Box>
              {/* Transaction Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Order ID
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedTransaksi.order_id}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Transaksi
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(selectedTransaksi.totaljual)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tanggal
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedTransaksi.tanggal)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Waktu Dibuat
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(selectedTransaksi.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pembayaran
                  </Typography>
                  <Chip
                    label={selectedTransaksi.pembayaran.toUpperCase()}
                    size="small"
                    color={getPaymentColor(selectedTransaksi.pembayaran)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedTransaksi.status_pembayaran.toUpperCase()}
                    size="small"
                    color={getStatusColor(selectedTransaksi.status_pembayaran)}
                  />
                </Grid>
              </Grid>

              {/* Customer Info */}
              <Typography variant="h6" sx={{ mb: 2 }}>
                Informasi Customer
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Nama
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaksi.Customer?.nama || selectedTransaksi.namaCustomer}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaksi.Customer?.email || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Telepon
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaksi.Customer?.telpon || '-'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Transaction Details */}
              <Typography variant="h6" sx={{ mb: 2 }}>
                Detail Barang
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nama Barang</TableCell>
                      <TableCell align="right">Jumlah</TableCell>
                      <TableCell align="right">Harga</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedTransaksi.TransaksiDetails?.map((detail) => (
                      <TableRow key={detail.uuid}>
                        <TableCell>
                          {detail.Barang?.namabarang || 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          {detail.jumlahbarang}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(detail.harga)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(detail.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailModal}>Tutup</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              // Handle edit from modal
              handleCloseDetailModal();
            }}
          >
            Edit Transaksi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};